import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { IScheduleItem, ScheduleItemInputType, ScheduleType } from 'types';

dayjs.locale('ko');

export const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

export type DraftType = Omit<
  ScheduleItemInputType,
  'endTime' | 'summary' | 'type' | 'description' | 'place' | 'notice'
> & {
  endTime: string;
  summary: string;
  type: ScheduleType | '';
  description: string;
  place: string;
  notice: string;
};

export const emptyDraft = (date: string): DraftType => ({
  date,
  startTime: '',
  endTime: '',
  title: '',
  summary: '',
  type: '',
  description: '',
  place: '',
  notice: '',
});

export const toDraft = (item: IScheduleItem, withId = true): DraftType => ({
  ...(withId && { id: item.id }),
  date: item.date,
  startTime: item.startTime,
  endTime: item.endTime ?? '',
  title: item.title,
  summary: item.summary ?? '',
  type: item.type ?? '',
  description: item.description ?? '',
  place: item.place ?? '',
  notice: item.notice ?? '',
});

const nullable = (value: string) => (value.trim() ? value.trim() : null);

export const toInput = (draft: DraftType): ScheduleItemInputType => {
  const endTime = draft.endTime.trim();
  return {
    ...(draft.id && { id: draft.id }),
    date: draft.date,
    startTime: draft.startTime.trim(),
    endTime: endTime && endTime !== draft.startTime.trim() ? endTime : null,
    title: draft.title.trim(),
    summary: nullable(draft.summary),
    type: draft.type || null,
    description: nullable(draft.description),
    place: nullable(draft.place),
    notice: nullable(draft.notice),
  };
};

export const normalizeTime = (value: string) => {
  const v = value.trim();
  if (/^\d{4}$/.test(v)) return `${v.slice(0, 2)}:${v.slice(2)}`;
  if (/^\d{1}:\d{2}$/.test(v)) return `0${v}`;
  return v;
};

const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const isNextDay = (
  item: Pick<ScheduleItemInputType, 'startTime' | 'endTime'>,
) => !!item.endTime && item.endTime < item.startTime;

export const formatTimeRange = (
  item: Pick<ScheduleItemInputType, 'startTime' | 'endTime'>,
) => {
  if (!item.endTime) return item.startTime;
  return `${item.startTime}–${isNextDay(item) ? '익일 ' : ''}${item.endTime}`;
};

const toRange = (item: ScheduleItemInputType) => {
  const start = toMinutes(item.startTime);
  if (!item.endTime) return [start, start];
  const end = toMinutes(item.endTime);
  return [start, end < start ? end + 24 * 60 : end];
};

const isOverlapped = (a: ScheduleItemInputType, b: ScheduleItemInputType) => {
  const [as, ae] = toRange(a);
  const [bs, be] = toRange(b);
  if (as === ae && bs === be) return as === bs;
  if (as === ae) return bs <= as && as < be;
  if (bs === be) return as <= bs && bs < ae;
  return as < be && bs < ae;
};

export const findOverlaps = (
  target: ScheduleItemInputType,
  items: ScheduleItemInputType[],
) =>
  items.filter(
    (item) =>
      item.date === target.date &&
      (!target.id || item.id !== target.id) &&
      isOverlapped(target, item),
  );

const compare = (a: ScheduleItemInputType, b: ScheduleItemInputType) =>
  a.date === b.date
    ? a.startTime.localeCompare(b.startTime)
    : a.date.localeCompare(b.date);

export const mergeItem = (
  items: ScheduleItemInputType[],
  target: ScheduleItemInputType,
) => {
  const exists = !!target.id && items.some((v) => v.id === target.id);
  const next = exists
    ? items.map((v) => (v.id === target.id ? target : v))
    : [...items, target];
  return [...next].sort(compare);
};

export const formatDayLabel = (date: string, index: number) =>
  `DAY ${index + 1}  ${dayjs(date).format('MM.DD (ddd)')}`;

export const formatPeriod = (dates: string[]) => {
  if (dates.length === 0) return '-';
  const format = 'YYYY.MM.DD (ddd)';
  const first = dayjs(dates[0]).format(format);
  if (dates.length === 1) return first;
  return `${first} ~ ${dayjs(dates[dates.length - 1]).format(format)}`;
};

export const hasDetail = (item: ScheduleItemInputType) =>
  !!(item.description || item.place || item.notice);
